/* ============================================================
   cart-kaartje-cleanup.js
   ------------------------------------------------------------
   Verwijdert overtollige "Kaartje N" properties uit een line item
   wanneer het aantal omlaag wordt gezet (bv. quantity 3 → 2 zou
   normaal "Kaartje 3" laten staan in de order).

   Werkt op zowel /cart pagina als in de cart drawer.
   ============================================================ */
(function () {
  'use strict';

  /* In-flight request tracker per line, voorkomt dubbele cleanups bij snel klikken. */
  var pending = {};

  /**
   * Filter properties: gooi "Kaartje N" weg waar N > newQuantity.
   * Andere properties (bv. handmatige overige) blijven behouden.
   */
  function filterKaartjes(properties, newQuantity) {
    var result = {};
    var changed = false;
    Object.keys(properties || {}).forEach(function (key) {
      var match = /^Kaartje\s+(\d+)$/.exec(key);
      if (match) {
        var num = parseInt(match[1], 10);
        if (num > newQuantity) {
          changed = true;
          return; /* Skip — wordt verwijderd */
        }
      }
      result[key] = properties[key];
    });
    return changed ? result : null;
  }

  /**
   * Sync kaartje-line items met de quantity van hun parent. Een kaartje hoort
   * altijd 1-op-1 bij een product — totaal aantal kaartjes voor dit bundle
   * moet exact gelijk zijn aan parent.quantity.
   *
   * Bij meer kaartjes dan producten → cap omlaag (verminder of verwijder lines).
   * Bij minder kaartjes dan producten → voeg extra kaartjes toe via /cart/add.js.
   */
  function syncKaartjesForBundle(parentItem, newParentQty, allItems) {
    if (!parentItem || !parentItem.properties || !parentItem.properties._bundle) {
      return Promise.resolve();
    }
    var bundleKey = parentItem.properties._bundle;

    /* Verzamel alle kaartje-lines voor dit bundle. We gebruiken line-keys
       (it.key) i.p.v. indices, want indices schuiven op bij verwijderingen. */
    var kaartjeLines = [];
    var totalKaartjes = 0;
    var kaartjeVariantId = null;
    allItems.forEach(function (it) {
      if (!it || !it.product) return;
      if (it.product.handle !== 'persoonlijk-kaartje') return;
      if (!it.properties || it.properties._kaartje_voor !== bundleKey) return;
      kaartjeLines.push({ key: it.key, qty: it.quantity });
      totalKaartjes += it.quantity;
      kaartjeVariantId = it.variant_id || it.id;
    });

    if (totalKaartjes === newParentQty) return Promise.resolve();

    /* TEKORT → extra kaartje(s) toevoegen */
    if (totalKaartjes < newParentQty) {
      if (!kaartjeVariantId) return Promise.resolve();
      var tekort = newParentQty - totalKaartjes;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{
            id: kaartjeVariantId,
            quantity: tekort,
            properties: { _kaartje_voor: bundleKey },
          }],
        }),
      }).then(function (r) { return r.json(); });
    }

    /* OVERSCHOT → kaartjes afbouwen vanaf de laatste line. We bouwen een
       enkel /cart/update.js payload op met alle nieuwe quantities; dat is
       atomair en heeft geen last van verschuivende indices. */
    var overschot = totalKaartjes - newParentQty;
    var updates = {};
    for (var k = kaartjeLines.length - 1; k >= 0 && overschot > 0; k--) {
      var line = kaartjeLines[k];
      var afbouw = Math.min(line.qty, overschot);
      updates[line.key] = line.qty - afbouw;
      overschot -= afbouw;
    }
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) { return r.json(); });
  }

  /**
   * Fetch huidige cart, vindt het line item, filtert properties en
   * verstuurt een nieuwe change.js request alleen als er iets te wissen is.
   * Daarnaast: cap kaartjes voor de bundle op de nieuwe parent quantity.
   */
  function cleanupLine(lineIndex, newQuantity) {
    if (!lineIndex || pending[lineIndex]) return;
    pending[lineIndex] = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var item = cart.items[lineIndex - 1];
        if (!item) return null;

        var syncWork = syncKaartjesForBundle(item, newQuantity, cart.items);

        if (!item.properties) return syncWork;
        var filtered = filterKaartjes(item.properties, newQuantity);
        if (!filtered) return syncWork;

        return syncWork.then(function () {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              line: lineIndex,
              quantity: newQuantity,
              properties: filtered,
            }),
          }).then(function (r) { return r.json(); });
        });
      })
      .then(function () {
        if (window.publish && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-kaartje-cleanup' });
        }
      })
      .catch(function (err) {
        console.warn('[kaartje-cleanup]', err);
      })
      .finally(function () {
        delete pending[lineIndex];
      });
  }

  /**
   * Orphan-kaartjes opruimen.
   *
   * Een "orphan" is een kaartje (product-handle persoonlijk-kaartje) waarvan
   * de _kaartje_voor property naar een _bundle wijst die niet meer voorkomt
   * in de cart — bv. omdat het parent product is verwijderd.
   *
   * Zonder cleanup blijven kaartjes in de cart hangen: ze zijn onzichtbaar
   * op /cart en in de drawer (de templates filteren ze) maar tellen wél mee
   * in cart.total_price → klant betaalt voor losse kaartjes en de drawer
   * toont een ander totaal dan de cart-pagina.
   */
  var orphanCleanupRunning = false;
  function cleanupOrphanKaartjes() {
    if (orphanCleanupRunning) return Promise.resolve();
    orphanCleanupRunning = true;
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var parentBundles = {};
        (cart.items || []).forEach(function (it) {
          if (!it || !it.product_title) return;
          if (it.handle === 'persoonlijk-kaartje') return;
          if (it.properties && it.properties._bundle) {
            parentBundles[it.properties._bundle] = true;
          }
        });

        var updates = {};
        var orphanCount = 0;
        (cart.items || []).forEach(function (it) {
          if (!it || it.handle !== 'persoonlijk-kaartje') return;
          var voor = it.properties && it.properties._kaartje_voor;
          if (!voor || !parentBundles[voor]) {
            updates[it.key] = 0;
            orphanCount += 1;
          }
        });

        if (orphanCount === 0) return null;

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates }),
        }).then(function (r) { return r.json(); }).then(function () {
          /* Trigger Dawn's sync zodat drawer + cart-pagina opnieuw rendert.
             PUB_SUB_EVENTS is een classic-script `const` — niet op window,
             dus we refereren 'm direct via typeof-check. */
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-kaartje-cleanup' });
          }
        });
      })
      .catch(function (err) { console.warn('[kaartje-orphan-cleanup]', err); })
      .finally(function () { orphanCleanupRunning = false; });
  }

  /**
   * Onderschep quantity-input change-events op cart-items en cart-drawer-items.
   * We luisteren in de capture-fase op de hostende cart-items / cart-drawer-items
   * zodat we afvuren ná Shopify's eigen debouncede change handler.
   */
  function attachListeners() {
    document.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || input.classList === undefined) return;
      if (!input.classList.contains('quantity__input')) return;
      /* Alleen op cart pagina of drawer (niet op productpagina-quantity) */
      var inCart = input.closest('cart-items') || input.closest('cart-drawer-items');
      if (!inCart) return;

      var lineIndex = parseInt(input.dataset.index, 10);
      var newQty = parseInt(input.value, 10);
      if (!lineIndex || isNaN(newQty)) return;
      if (newQty < 1) return; /* 0 = remove → orphan-cleanup pakt 'm via cartUpdate event */

      /* Wacht kort tot Shopify zijn eigen debounced change-call heeft afgerond */
      setTimeout(function () { cleanupLine(lineIndex, newQty); }, 350);
    }, true);

    /* Run orphan-cleanup na ELKE cart update (remove parent, update qty, etc.).
       Slaat eigen events over om loops te voorkomen.
       PUB_SUB_EVENTS is een `const` in constants.js — niet op window, dus
       refereren via lexical lookup met typeof-guard. */
    function subscribeToCartUpdates() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'cart-kaartje-cleanup') return;
        setTimeout(cleanupOrphanKaartjes, 250);
      });
      return true;
    }
    if (!subscribeToCartUpdates()) {
      /* Dawn's PUB_SUB module laadt soms iets later — probeer kort opnieuw */
      var tries = 0;
      var iv = setInterval(function () {
        tries += 1;
        if (subscribeToCartUpdates() || tries > 20) clearInterval(iv);
      }, 200);
    }

    /* Vangnet: ook puur DOM events oppikken — sommige theme-scripts publishen
       via document events. Dat dekt het geval dat pubsub niet beschikbaar is. */
    ['cart:refresh', 'cart:updated', 'cart:added'].forEach(function (evt) {
      document.addEventListener(evt, function () {
        setTimeout(cleanupOrphanKaartjes, 250);
      });
    });

    /* Defensieve eerste pass bij page-load voor reeds bestaande orphans
       (bv. uit een vorige sessie). */
    setTimeout(cleanupOrphanKaartjes, 800);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachListeners);
  } else {
    attachListeners();
  }
}());
